// @LTG-ID: lib-use-utm-passthrough
// @STATUS: BUILT @SHARED @EXTERNAL
// @HERMETIC: true
// @DOCS: 10_DECISIONS.md#ADR-005, 10_DECISIONS.md#ADR-008
// @CHANGED: 2026-02 session_002 (CHG-0030)
// @OWNER: web
//
// Defensive safety effect: re-decorates any raw <a href*="app.letstalkgrowth.com"> in the DOM
// at 100/500/1500/3000ms after mount, and continuously thereafter via MutationObserver,
// using the SAME shared decorator as ExternalCTA (lib/url-utils.decorateAppLtgUrl).
//
// Two layers of belt-and-suspenders:
//   1. Time-based: catches anchors present at first paint and any added during the
//      first ~3 seconds. Survives React re-mount races.
//   2. MutationObserver: catches anchors added or whose href is mutated by ANY code
//      (third-party widgets, dev-tools, future ad-hoc DOM injection). The primary
//      path for our own anchors is still <ExternalCTA> at render time.
//
// Idempotence: decorateAppLtgUrl is deterministic, so re-running on an already-decorated
// href produces the same string. We compare and only assign when different, which also
// prevents the MutationObserver from looping on its own href writes.

import { useEffect } from "react";
import {
    captureAttributionSnapshot,
    captureFirstTouchContext,
    decorateAppLtgUrl,
    getInboundSector,
} from "./url-utils";

function decorateAllNow() {
    if (typeof document === "undefined") return;
    const sector = getInboundSector();
    const componentParams = sector ? { sector } : {};
    try {
        document
            .querySelectorAll('a[href*="app.letstalkgrowth.com"]')
            .forEach((a) => {
                try {
                    const next = decorateAppLtgUrl(a.href, componentParams);
                    if (next && next !== a.href) a.href = next;
                } catch (_e) {
                    // ignore single-anchor failures
                }
            });
    } catch (_e) {
        // ignore
    }
}

export function useUtmPassthrough() {
    useEffect(() => {
        // 0) Seed sessionStorage attribution snapshot (first-hop-wins). See ADR-009.
        captureAttributionSnapshot();

        // 0.5) CHG-0113 · Seed first-touch metadata (referrer, landing_page,
        //      session_id, first_touch_at). No-op on subsequent mounts within
        //      the same sessionStorage lifetime. Enables campaign-source
        //      attribution on POST /api/contact submissions.
        captureFirstTouchContext();

        // 1) Synchronous first pass at mount, for anchors that already exist.
        decorateAllNow();

        // 2) Progressive retries to catch slow React commits and 3rd-party DOM mutations.
        const timers = [
            window.setTimeout(decorateAllNow, 100),
            window.setTimeout(decorateAllNow, 500),
            window.setTimeout(decorateAllNow, 1500),
            window.setTimeout(decorateAllNow, 3000),
        ];

        // 3) MutationObserver — coalesce changes into one rAF tick so we never busy-loop.
        let rafToken = 0;
        const schedule = () => {
            if (rafToken) return;
            rafToken = window.requestAnimationFrame(() => {
                rafToken = 0;
                decorateAllNow();
            });
        };

        const observer = new MutationObserver((mutations) => {
            for (const m of mutations) {
                if (m.type === "childList") {
                    for (const node of m.addedNodes) {
                        if (node.nodeType !== 1) continue;
                        if (
                            (node.matches &&
                                node.matches('a[href*="app.letstalkgrowth.com"]')) ||
                            (node.querySelector &&
                                node.querySelector('a[href*="app.letstalkgrowth.com"]'))
                        ) {
                            schedule();
                            return;
                        }
                    }
                } else if (m.type === "attributes" && m.attributeName === "href") {
                    const t = m.target;
                    if (
                        t &&
                        t.matches &&
                        t.matches('a[href*="app.letstalkgrowth.com"]')
                    ) {
                        schedule();
                        return;
                    }
                }
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["href"],
        });

        return () => {
            timers.forEach((t) => window.clearTimeout(t));
            if (rafToken) window.cancelAnimationFrame(rafToken);
            observer.disconnect();
        };
    }, []);
}
